/**
 * Audio widget — upload a file or record straight in the browser.
 *
 * Mirrors the app's own AudioInputWidget (upload/record tabs, inline player,
 * Danish permission errors) with the SPA's React/shadcn machinery stripped out.
 */

import type { FunctionComponent } from 'preact'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'preact/hooks'
import {
  type FileJSON,
  createFileJSON,
  fileJSONToDataUrl,
  parseAnyFileFormat,
  readFileAsDataURL,
} from '../../core/file-value'
import { FileUploaderContext } from '../../core/upload'
import { PickerButton } from './PickerButton'
import { usePickFiles } from './use-pick-files'

interface AudioInputProps {
  value: unknown
  onChange: (value: FileJSON | null) => void
  accept?: string | null
  maxSizeMB?: number | null
  disabled?: boolean
}

const DEFAULT_ACCEPT = 'audio/mp3,audio/mpeg,audio/wav,audio/m4a,audio/webm,audio/ogg,audio/flac'
const MIN_RECORDING_BYTES = 100

/** The first container the browser will actually give us. */
function pickRecordingMimeType(): string {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']
  for (const candidate of candidates) {
    if (MediaRecorder.isTypeSupported(candidate)) return candidate
  }
  return 'audio/webm'
}

function extensionFor(mimeType: string): string {
  if (mimeType.includes('webm')) return 'webm'
  if (mimeType.includes('mp4')) return 'm4a'
  return 'ogg'
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}

// getUserMedia is unavailable on an insecure origin, which is the likeliest
// cause on an embedding site that is still served over plain http.
const INSECURE_ORIGIN_MESSAGE =
  'Optagelse kræver, at siden serveres over https. Upload en lydfil i stedet.'

function recordingError(err: unknown): string {
  const error = err as { name?: string; message?: string }
  if (error?.name === 'NotAllowedError') {
    return 'Mikrofonadgang blev afvist. Tillad adgang til mikrofonen i browserens indstillinger og prøv igen.'
  }
  if (error?.name === 'NotFoundError') {
    return 'Der blev ikke fundet nogen mikrofon. Tilslut en mikrofon og prøv igen.'
  }
  if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
    return INSECURE_ORIGIN_MESSAGE
  }
  return `Optagelsen kunne ikke startes: ${error?.message ?? 'Ukendt fejl'}`
}

export const AudioInput: FunctionComponent<AudioInputProps> = ({
  value,
  onChange,
  accept,
  maxSizeMB,
  disabled,
}) => {
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<number | null>(null)

  const [mode, setMode] = useState<'upload' | 'record'>('upload')
  const [isRecording, setIsRecording] = useState(false)
  const [duration, setDuration] = useState(0)

  // One error slot for both halves: a failed pick and a failed recording are
  // shown in the same place.
  const upload = useContext(FileUploaderContext)
  // An uploaded file is a reference with no bytes in the page, and a visitor
  // cannot fetch it back. So the player plays the file the visitor picked or
  // recorded, from a local object URL, for as long as it is the value.
  const [localAudio, setLocalAudio] = useState<{ fileId: string; url: string } | null>(null)
  const keepLocalCopy = useCallback((value: FileJSON, source: Blob) => {
    setLocalAudio((previous) => {
      if (previous) URL.revokeObjectURL(previous.url)
      return value.file_id ? { fileId: value.file_id, url: URL.createObjectURL(source) } : null
    })
  }, [])
  useEffect(() => () => {
    if (localAudio) URL.revokeObjectURL(localAudio.url)
  }, [localAudio])

  const { inputRef, handlePick, reading, error, setError } = usePickFiles(
    maxSizeMB,
    (read, picked) => {
      if (picked[0]) keepLocalCopy(read[0], picked[0])
      onChange(read[0])
    },
    'Lydfilen'
  )
  const [saving, setSaving] = useState(false)

  const file = parseAnyFileFormat(value)
  // A 25 MB recording is a ~33 MB base64 string; rebuilding the data URL on
  // every keystroke elsewhere in the form is real work. The FileJSON object is
  // reference-stable, so memoise on it.
  const playbackSrc = useMemo(() => {
    if (file?.data) return fileJSONToDataUrl(file)
    return file?.file_id && localAudio?.fileId === file.file_id ? localAudio.url : ''
  }, [file, localAudio])

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) clearInterval(timerRef.current)
      const recorder = recorderRef.current
      if (recorder && recorder.state !== 'inactive') recorder.stop()
    }
  }, [])

  const stopRecording = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    const recorder = recorderRef.current
    if (recorder && recorder.state !== 'inactive') recorder.stop()
    setIsRecording(false)
  }, [])

  const startRecording = useCallback(async () => {
    setError(null)
    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setError(
        navigator.mediaDevices
          ? 'Optagelse understøttes ikke i denne browser. Upload en lydfil i stedet.'
          : INSECURE_ORIGIN_MESSAGE
      )
      return
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mimeType = pickRecordingMimeType()
      const recorder = new MediaRecorder(stream, { mimeType })
      recorderRef.current = recorder
      chunksRef.current = []

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data)
      }

      recorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop())
        const blob = new Blob(chunksRef.current, { type: mimeType })
        // The chunks are the whole recording; once the Blob owns the bytes,
        // keeping them in the ref is a second copy that lives until unmount.
        chunksRef.current = []
        recorderRef.current = null

        if (blob.size < MIN_RECORDING_BYTES) {
          setError('Optagelsen er tom. Kontrollér at mikrofonen virker, og prøv igen.')
          return
        }
        if (maxSizeMB && blob.size > maxSizeMB * 1024 * 1024) {
          setError(`Optagelsen overstiger grænsen på ${maxSizeMB} MB.`)
          return
        }

        // Drop the codec parameter: the wire format carries a plain MIME type,
        // and the transcription service keys off it.
        const baseMimeType = mimeType.split(';')[0]
        const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)
        const filename = `optagelse-${stamp}.${extensionFor(mimeType)}`
        const recorded = new File([blob], filename, { type: baseMimeType })

        setSaving(true)
        try {
          const stored = upload
            ? await upload(recorded)
            : createFileJSON(recorded, await readFileAsDataURL(recorded))
          keepLocalCopy(stored, recorded)
          onChange(stored)
          setError(null)
        } catch {
          setError('Optagelsen kunne ikke gemmes. Prøv igen.')
        } finally {
          setSaving(false)
        }
      }

      recorder.start(1000)
      setIsRecording(true)
      setDuration(0)
      timerRef.current = window.setInterval(() => setDuration((d) => d + 1), 1000)
    } catch (err) {
      setError(recordingError(err))
    }
  }, [maxSizeMB, onChange, setError, upload, keepLocalCopy])

  const clear = useCallback(() => {
    setError(null)
    onChange(null)
  }, [onChange, setError])

  if (file) {
    return (
      <div class="syv-audio-player">
        {playbackSrc ? (
          <audio class="syv-audio-element" src={playbackSrc} controls preload="metadata" />
        ) : (
          // A value with metadata but no bytes is what the API serves after
          // stripping a stored run's payload. Playback is impossible, so offer a
          // re-upload instead of a control that looks live and does nothing.
          <span class="syv-hint">Lyden er ikke gemt — vælg filen igen</span>
        )}
        <span class="syv-file-name">{file.filename || 'Lydfil'}</span>
        {!disabled && (
          <button type="button" class="syv-icon-button" aria-label="Fjern lydfil" onClick={clear}>
            ×
          </button>
        )}
      </div>
    )
  }

  return (
    <div class="syv-audio">
      <div class="syv-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'upload'}
          class={`syv-tab${mode === 'upload' ? ' syv-tab-active' : ''}`}
          disabled={disabled || isRecording}
          onClick={() => setMode('upload')}
        >
          Upload
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'record'}
          class={`syv-tab${mode === 'record' ? ' syv-tab-active' : ''}`}
          disabled={disabled}
          onClick={() => setMode('record')}
        >
          Optag
        </button>
      </div>

      {mode === 'upload' ? (
        <>
          <input
            ref={inputRef}
            type="file"
            accept={accept || DEFAULT_ACCEPT}
            disabled={disabled}
            onChange={handlePick}
            class="syv-file-native"
          />
          <PickerButton
            label="Vælg lydfil…"
            reading={reading}
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
          />
        </>
      ) : isRecording ? (
        <div class="syv-recording">
          <span class="syv-recording-dot" aria-hidden="true" />
          <span class="syv-recording-label">Optager…</span>
          <span class="syv-recording-time">{formatDuration(duration)}</span>
          <button type="button" class="syv-button syv-button-secondary" onClick={stopRecording}>
            Stop optagelse
          </button>
        </div>
      ) : (
        <button
          type="button"
          class="syv-button syv-button-secondary syv-file-trigger"
          disabled={disabled || saving}
          onClick={startRecording}
        >
          {saving ? (
            <>
              <span class="syv-spinner" /> Gemmer optagelsen…
            </>
          ) : (
            'Start optagelse'
          )}
        </button>
      )}

      {error && <div class="syv-error">{error}</div>}
      {maxSizeMB ? <span class="syv-hint">Maks. {maxSizeMB} MB</span> : null}
    </div>
  )
}
